import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button, Tooltip, Typography } from "@material-ui/core";
import { Pause } from "@material-ui/icons";
import CheckMark from "../../../../assets/icons/checkMark.png";
import Microphone from "../../../../assets/icons/Microphone.png";
import AudioWave from "../../../../assets/icons/audioWave.png";
import "./BookScreenQueueCard.scss";
import { RootState } from "../../../../state/configureStore";
import { startGettingReadyToRecord } from "../state/BookScreenState";
import { useSelector } from "react-redux";
import UploadService from "../../../../services/upload/UploadService";
import Shiitake from "shiitake";
import RecordedPage from "../../../../interfaces/RecordedPage";
import LogService from "../../../../services/LogService";
import OverflowButtons from "./OverflowButtons";
import StudentFacingData from "../../../../interfaces/StudentFacingData";
import StudentsStatsWidget from "./StudentStatsWidget";
import sortTranscripts from "../../../../utils/SortTranscripts";

interface QueueCardProps {
  pageTitle: string;
  pageIndex: number;
  accuracy?: number;
  wordsPerMin?: number;
  recordedPage?: RecordedPage;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onDeleteRecording: () => void;
  completePage: () => void;
  incompletePage: () => void;
  isSubmitted: boolean;
  isActive: boolean;
  timeRecorded: string;
  onSetPage: (pageIndex: number) => void;
  studentFacingData?: StudentFacingData;
}

interface MicAvatarProps {
  isCompleted?: boolean;
}
const MicAvatar = ({ isCompleted }: MicAvatarProps) => (
  <Avatar className={isCompleted ? "circleCheckMark" : "circleMicIcon"}>
    <img className="circleImage" src={isCompleted ? CheckMark : Microphone} alt={isCompleted ? "Icon" : "Record"} />
  </Avatar>
);

interface CardContentsProps {
  pageTitle: string;
  pageIndex: number;
  children: React.ReactNode;
}
const CardContents = ({ pageTitle, pageIndex, children }: CardContentsProps) => (
  <div className="cardText">
    <Typography component="div" className="pageTitle">
      <Shiitake lines={2}>{pageTitle || `Page ${pageIndex + 1}`}</Shiitake>
    </Typography>
    {children}
  </div>
);

const getIsCompleted = (completed?: boolean, completionPercentage?: number): boolean =>
  !!(completed || (completionPercentage && completionPercentage >= 0.95));

const getCardClassName = (isSubmitted: boolean, isCountingDown: boolean, isActive: boolean, isHovered: boolean) =>
  `${isSubmitted && !isCountingDown ? "submitted" : "queue"}Card ${
    isActive ? "active" : isHovered ? "hoveredCard" : ""
  }`;

const BookScreenQueueCard = (props: QueueCardProps) => {
  const [audioUrls, setAudioUrl] = useState([""]);
  const [audioDurations, setAudioDurations] = useState([0]);
  // If we have audio for this page, load it.
  useEffect(() => {
    //function to load the array of audio urls we get from storage, as well as a corresponding array for file durations
    const loadAudio = async (): Promise<void> => {
      if (props.recordedPage?.transcripts) {
        setAudioUrl([]);
        setAudioDurations([]);
        const transcriptEntries = Object.entries(props.recordedPage.transcripts).sort(sortTranscripts);
        const tempUrls: Array<[string, string]> = [];
        const tempDurations: Array<[string, number]> = [];
        //we iterate through each transcript and its associated section Id.

        for (const [sectionId, transcript] of transcriptEntries) {
          //if we are uploading, don't do anything cause otherwise will be 404 error
          if (!transcript?.uploading) {
            await UploadService.getPageAudio(props.recordedPage?.storageFolderPath || "", sectionId)
              .then(value => {
                tempUrls.push([sectionId, value]);
                tempDurations.push([sectionId, transcript.duration]);
              })
              .catch(err => {
                LogService.exception(err, "BookScreenQueueCard", "error in getting page audio");
              });
          }
        }

        //we sort the tempUrls and temp Durations by section Id
        Object.entries(tempUrls).sort(sortTranscripts);
        Object.entries(tempDurations).sort(sortTranscripts);

        //create variables to store our final sorted arrays.
        const finalUrls = [];
        const finalDurations = [];

        //loop through the temporary urls with their section ids and add them to our final ones.
        for (let j = 0; j < tempUrls.length; j++) {
          const url = tempUrls[j];
          const duration = tempDurations[j];

          if (url && duration) {
            finalUrls.push(url[1]);
            finalDurations.push(duration[1]);
          }
        }

        //set state
        setAudioUrl(finalUrls);
        setAudioDurations(finalDurations);
      }
    };
    loadAudio();
  }, [props.recordedPage?.transcripts, props.recordedPage?.storageFolderPath]);

  const isCompleted = getIsCompleted(props.recordedPage?.completed, props.recordedPage?.completionPercentage);

  //hover state
  const [isHovered, setIsHovered] = useState(false);
  const [buttonTimer, setButtonTimer] = useState(3);
  const [isCountingDown, setCountingDown] = useState(false);
  const buttonTimerRef = useRef(buttonTimer);
  const isCountingDownRef = useRef(isCountingDown);
  buttonTimerRef.current = buttonTimer;
  isCountingDownRef.current = isCountingDown;
  const state = useSelector((state: RootState) => state.bookScreenState);

  const recordClicked = () => {
    if (!state.isRecording) {
      startGettingReadyToRecord();
    } else if (state.isRecording) {
      props.onStopRecording();
    }
  };

  const { pageIndex, onStartRecording } = props;
  const { isGettingReadyToRecording, currentPageNumber } = state;

  useEffect(() => {
    if (!isCountingDownRef.current && isGettingReadyToRecording && currentPageNumber === pageIndex) {
      setCountingDown(true);
      setButtonTimer(3);

      const runCountDown = async () => {
        if (buttonTimerRef.current === 0) {
          onStartRecording();
          await setCountingDown(false);
        } else {
          await setButtonTimer(prevState => prevState - 1);
          setTimeout(runCountDown, 1000);
        }
      };

      setTimeout(() => {
        runCountDown();
      }, 1000);
    }
  }, [pageIndex, onStartRecording, isGettingReadyToRecording, isCountingDown, currentPageNumber]);

  const showButtons = isHovered || props.isActive;
  const cardClassName = getCardClassName(props.isSubmitted, isCountingDown, props.isActive, isHovered);

  return (
    <div
      onClick={() => {
        if (!props.isActive) {
          props.onSetPage(props.pageIndex);
        }
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      {(!state.isRecording && !state.isGettingReadyToRecording) || !props.isActive ? ( //not recording or getting ready to
        <div className={cardClassName}>
          <MicAvatar isCompleted={isCompleted} />
          <CardContents pageTitle={props.pageTitle} pageIndex={props.pageIndex}>
            {props.isSubmitted ? (
              <StudentsStatsWidget
                studentFacingData={props.studentFacingData}
                accuracy={props.accuracy}
                wordsPerMin={props.wordsPerMin}
              />
            ) : (
              <Typography component="div" className="words">
                <Shiitake lines={2}>{props.pageTitle}</Shiitake>
              </Typography>
            )}
          </CardContents>
          {showButtons &&
            (props.isSubmitted ? (
              <OverflowButtons
                isCompleted={isCompleted}
                onDeleteRecording={props.onDeleteRecording}
                onStartRecording={props.onStartRecording}
                audioUrls={audioUrls}
                audioDurations={audioDurations}
              />
            ) : (
              <div className="buttonContainer">
                <Button className="button" onClick={recordClicked}>
                  <Tooltip title="Begin Recording">
                    <Typography className="buttonText">Start</Typography>
                  </Tooltip>
                </Button>
              </div>
            ))}
        </div>
      ) : state.isGettingReadyToRecording && props.isActive ? (
        <div className={cardClassName}>
          <Avatar className="activeAvatar">
            <div className="buttonText">{buttonTimer.toString()}</div>
          </Avatar>
          <Typography className="pageTitle">Start Speaking in...</Typography>
        </div>
      ) : (
        <div className="active queueCard">
          <Tooltip title="Take a break from reading">
            <Button onClick={props.onStopRecording}>
              <Avatar className="activeAvatar">
                <Pause />
              </Avatar>
            </Button>
          </Tooltip>
          <Typography className="rightAudioDuration">{"(" + props.timeRecorded + ")"}</Typography>
          <div className="audioWaveContainer">
            <img src={AudioWave} alt="Audio Visualization" className="audioWaveImage" />
            <img src={AudioWave} alt="Audio Visualization" className="audioWaveImage" />
            <img src={AudioWave} alt="Audio Visualization" className="audioWaveImage" />
          </div>
        </div>
      )}
    </div>
  );
};

export default BookScreenQueueCard;
